import type { SourcingPlatform } from '@/infrastructure/FulfillmentAdminClient';
import { DELIVERY_COMPANIES } from '@/pages/oms/orders/utils/deliveryCompany';
import { SOURCING_PLATFORMS } from '@/utils/fulfillmentSourcingDetail';

/** The agent-filled cells of the open row, held as text so a half-typed number is not a value yet. */
export interface LineDraft {
  totalQuantity: string;
  /** The line's display size (SSD-7514); empty puts back the size the line was taken in with. */
  size: string;
  purchaseOrderNumber: string;
  purchaseOrderDate: string;
  /** A SourcingPlatform, or empty for none. */
  purchasePlatform: string;
  productCost: string;
  lineTrackingNumber: string;
  lineDeliveryCompany: string;
}

export type LineEditField = keyof LineDraft;

/** The sentence standing against each field that has one; a field with nothing wrong is absent. */
export type LineEditErrors = Partial<Record<LineEditField, string>>;

/** What a row is read from; only the cells the editor opens. */
export interface EditableLine {
  totalQuantity: number;
  size?: string | null;
  purchaseOrderNumber?: string | null;
  purchaseOrderDate?: string | null;
  purchasePlatform?: SourcingPlatform | null;
  productCost?: number | null;
  lineTrackingNumber?: string | null;
  lineDeliveryCompany?: string | null;
}

export const EMPTY_LINE_DRAFT: LineDraft = {
  totalQuantity: '',
  size: '',
  purchaseOrderNumber: '',
  purchaseOrderDate: '',
  purchasePlatform: '',
  productCost: '',
  lineTrackingNumber: '',
  lineDeliveryCompany: '',
};

/** The server's cap on a line's display size (SSD-7514). */
export const LINE_SIZE_MAX_LENGTH = 64;

/** One sentence per field, shared by the check before the save and the server's refusal after it. */
export const LINE_EDIT_MESSAGES: Record<LineEditField, string> = {
  totalQuantity: 'Quantity must be a whole number above zero.',
  size: `Size can be up to ${LINE_SIZE_MAX_LENGTH} characters.`,
  purchaseOrderNumber: 'Order number can be up to 128 characters.',
  purchaseOrderDate: 'Order date must be a date, such as 2026-07-01.',
  purchasePlatform: 'Platform must be one of the listed platforms.',
  productCost: 'Product cost must be a number, 0 or more.',
  lineTrackingNumber: 'Tracking number can be up to 128 characters.',
  lineDeliveryCompany: 'Delivery company must be one of the listed OMS companies or Unknown.',
};

const LINE_EDIT_FIELDS = Object.keys(LINE_EDIT_MESSAGES) as LineEditField[];

/** The cap the server puts on the two free-text numbers. The CP link is not edited here (SSD-7588). */
const REFERENCE_MAX_LENGTH = 128;

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A day that exists: the shape alone would let 2026-02-31 through. */
const isCalendarDate = (text: string): boolean => {
  const parts = CALENDAR_DATE.exec(text);
  if (!parts) {
    return false;
  }
  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/**
 * The stored order date as a date input takes it. The server hands the value back as free text, so a
 * date and an instant both read as their day, and anything else opens as an empty box.
 */
export const toDateInputValue = (stored?: string | null): string => {
  const day = (stored ?? '').trim().slice(0, 10);
  const rest = (stored ?? '').trim().slice(10);
  return isCalendarDate(day) && (rest === '' || rest.startsWith('T')) ? day : '';
};

/** The row as the editor opens it, which is also what a draft is compared against to find what was retyped. */
export const lineDraftOf = (line: EditableLine): LineDraft => ({
  totalQuantity: String(line.totalQuantity),
  size: line.size ?? '',
  purchaseOrderNumber: line.purchaseOrderNumber ?? '',
  purchaseOrderDate: toDateInputValue(line.purchaseOrderDate),
  purchasePlatform: line.purchasePlatform ?? '',
  productCost: line.productCost == null ? '' : String(line.productCost),
  lineTrackingNumber: line.lineTrackingNumber ?? '',
  lineDeliveryCompany: line.lineDeliveryCompany ?? '',
});

/** Whether the value breaks its field's rule. An empty box clears the field, so only the quantity refuses one. */
const breaksRule = (field: LineEditField, value: string): boolean => {
  const trimmed = value.trim();
  if (field === 'totalQuantity') {
    const quantity = Number(trimmed);
    return trimmed === '' || !Number.isInteger(quantity) || quantity <= 0;
  }
  // Only a truly empty text field clears it; whitespace still travels and must meet its rule.
  if (value === '' || (field === 'productCost' && trimmed === '')) {
    return false;
  }
  switch (field) {
    case 'size':
      return value.trim().length > LINE_SIZE_MAX_LENGTH;
    case 'purchaseOrderNumber':
    case 'lineTrackingNumber':
      return value.length > REFERENCE_MAX_LENGTH;
    case 'purchaseOrderDate':
      return !isCalendarDate(value);
    case 'purchasePlatform':
      return !(SOURCING_PLATFORMS as string[]).includes(value);
    case 'lineDeliveryCompany':
      return value !== 'UNASSIGNED' && !DELIVERY_COMPANIES.some((company) => company.code === value);
    case 'productCost': {
      const cost = Number(trimmed);
      return !Number.isFinite(cost) || cost < 0;
    }
  }
};

/** The rule this value breaks, or nothing. */
export const describeLineEditField = (field: LineEditField, value: string): string | null =>
  breaksRule(field, value) ? LINE_EDIT_MESSAGES[field] : null;

/**
 * Every rule the draft breaks, among the fields that were retyped. An untouched field never travels,
 * so an older value the rules would refuse today must not lock the rest of the row.
 */
export const lineEditErrorsOf = (draft: LineDraft, stored: LineDraft): LineEditErrors => {
  const errors: LineEditErrors = {};
  for (const field of LINE_EDIT_FIELDS) {
    const message = draft[field] === stored[field] ? null : describeLineEditField(field, draft[field]);
    if (message) {
      errors[field] = message;
    }
  }
  return errors;
};

const sentencesOf = (message: string | string[] | undefined): string[] =>
  Array.isArray(message) ? message : message ? [message] : [];

/** The server opens a rejected field's sentence with the field's own name. */
const fieldNamedBy = (sentence: string): LineEditField | undefined =>
  LINE_EDIT_FIELDS.find((field) => sentence.toLowerCase().startsWith(`${field.toLowerCase()} `));

/**
 * The server's refusal, read back onto the fields it names in the words this screen uses for them.
 * Its own sentence spells the wire name ("purchaseOrderDate must be a valid ISO 8601 date string").
 */
export const lineEditRefusalsOf = (message: string | string[] | undefined): LineEditErrors => {
  const refusals: LineEditErrors = {};
  for (const sentence of sentencesOf(message)) {
    const field = fieldNamedBy(sentence);
    if (field) {
      refusals[field] = LINE_EDIT_MESSAGES[field];
    }
  }
  return refusals;
};

/**
 * The same refusal as one line for the toast. A sentence that names no line field is kept as the
 * server wrote it; a refusal that names none at all is not a line-edit refusal and yields nothing.
 */
export const describeLineEditRefusal = (message: string | string[] | undefined): string | undefined => {
  const sentences = sentencesOf(message);
  if (!sentences.some((sentence) => fieldNamedBy(sentence))) {
    return undefined;
  }
  const readable = sentences.map((sentence) => {
    const field = fieldNamedBy(sentence);
    return field ? LINE_EDIT_MESSAGES[field] : sentence;
  });
  return [...new Set(readable)].join(' · ');
};
